import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import imageCompression from 'browser-image-compression';
import './InspectionView.scss';
import { Icon } from '@iconify/react';

import {
  cardCheckBoxDetails,
  complianceCheckboxes,
  engineCheckboxes,
  exteriorCheckboxes,
  interiorCheckboxes,
  safetyCheckboxes,
  underCarriageCheckboxes,
  vehicleCheckboxes
} from '../../../redux/reducer/truckManagementSlices/inspectionForm';
import {
  setInspectionDetails,
  uploadFileAction
} from '../../../redux/reducer/truckManagementSlices/inspectionSlice';

import { truckNumbers } from '../../../redux/reducer/truckManagementSlices/maintenanceSlice';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import CustomMultiCard from '../../../../../common/components/customized/customcheckbox/CustomMultiCard';
import CustomCardcheckbox from '../../../../../common/components/customcardcheckbox/customcardcheckbox';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import { convertToEpoch, useAbort } from '../../../../../utils/commonFunctions';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import CustomLinearProgress from '../../../../../common/components/customlinearprogress/CustomLinearProgress';
import CustomCheckBox from '../../../../../common/components/customized/customcheckbox/CustomCheckBox';
import CustomDateCalendar from '../../../../../common/components/customized/customcalendar/CustomCalendar';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';

const InspectionView = ({
  setChecked,
  setInspection,
  setInspectionName,
  setInspectionId,
  viewInspection,
  filteredRow
}: any) => {
  const { data, isLoading } = useAppSelector((state: any) => state.addInspection);
  const { data: truckNumber } = useAppSelector(state => state.truckNumbers);
  const [cardDetails, setCardDetails] = useState<any>(cardCheckBoxDetails);
  const [fileUploads, setFileUploads] = useState([]);
  const [inspector, setInspector] = useState();
  const [inspectorId, setInspectorId] = useState<any>();
  const [progressCount, setProgressCount] = useState(0);
  const createAbort = useAbort();
  const textField = [
    {
      id: 'dateOfInspection',
      label: 'Date of Inspection',
      placeholder: 'Date of Inspection'
    },
    { id: 'vin', label: 'Vehicle Number', placeholder: 'Vehicle Number' },
    { id: 'inspectorName', label: 'Inspector Name', placeholder: 'Inspector Name' }
  ];

  const handleFromDate = (date: any) => {
    setValue('dateOfInspection', date?.$d);
    clearErrors('dateOfInspection');
  };

  const onboardSchema = yup.object().shape({
    ...exteriorCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...interiorCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...engineCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...underCarriageCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...safetyCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...vehicleCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    ...complianceCheckboxes.reduce((schema, field) => {
      schema[field.field] = yup
        .string()
        .oneOf(['Yes', 'No', 'N/A'], ' required')
        .required(' required');
      return schema;
    }, {} as any),
    dateOfInspection: yup
      .date()
      .required('DOT date is required')
      .typeError('DOT date is required'),
    vin: yup.string().required('VIN is required'),
    inspectorName: yup.string().trim().required('Inspector Name is required'),
    descriptions: yup.array().of(
      yup.object().shape({
        description: yup.string().notRequired()
      })
    )
  });

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(onboardSchema),
    defaultValues: {
      descriptions: [
        {
          description: ''
        }
      ]
    }
  });
  const vehicleDetails = truckNumber?.map((item: any) => ({
    id: item,
    label: item.toUpperCase()
  }));

  // function to handle multi select checkboxes
  const handleSelect = (
    value: string,
    list: any,
    cardindex: number,
    cardtitle: string
  ) => {
    setCardDetails((prev: any) => {
      let updatedCards = [...prev];
      let newCard = updatedCards
        .filter((card: any) => {
          if (card.title === cardtitle) {
            return card;
          }
        })
        .map((card: any) => ({
          ...card,
          category: card.category?.map((check: any) => ({
            ...check,
            checked: value === check.value ? true : false
          })),
          categoryList: card.categoryList?.map((categorylist: any) => ({
            ...categorylist,
            categorycheck: categorylist?.categorycheck?.map((item: any) => ({
              ...item,
              checked: value === item?.name ? true : false
            }))
          }))
        }));
      updatedCards[cardindex] = newCard[0];
      return updatedCards;
    });
    list.forEach((item: any) => {
      setValue(item.field, value);
      clearErrors(item.field);
    });
  };

  // function to handle single select checkboxes
  const handleCheckbox = (
    label: string,
    list: any,
    cardindex: number,
    cardtitle: string,
    checkboxName: string
  ) => {
    setCardDetails((prev: any) => {
      let updatedCards = [...prev];
      let newCard = updatedCards
        .filter((card: any) => {
          if (card.title === cardtitle) {
            return card;
          }
        })
        .map((card: any) => ({
          ...card,
          category: card.category?.map((check: any) => ({
            ...check,
            checked: false
          })),
          categoryList: card.categoryList?.map((list: any) => ({
            ...list,
            categorycheck: list?.categorycheck?.map((item: any) => ({
              ...item,
              checked:
                item.name === label && list?.name === checkboxName
                  ? true
                  : item.name !== label && list?.name === checkboxName
                  ? false
                  : item.checked
            }))
          }))
        }));
      updatedCards[cardindex] = newCard[0];
      return updatedCards;
    });
    list.forEach((item: any, index: number) => {
      if (checkboxName === item?.name) {
        setValue(item.field, label);
        clearErrors(item.field);
      }
    });
  };

  const dispatch = useAppDispatch();

  const fileUploadHook = (fields: any, files: any) => {
    let uploadedFiles = files
      .filter((file: any) => {
        if (Object.keys(fields).includes(file.field)) return file;
      })
      .map((documents: any) => {
        documents[documents.field] = [documents.document];
        return documents;
      })
      .map(({ field, document, documentFile, ...rest }: any) => {
        return rest;
      });

    let newFiles =
      uploadedFiles?.length > 0
        ? uploadedFiles?.reduce((currentObject: any, nextObject: any) => {
            return { ...currentObject, ...nextObject };
          })
        : {};
    return newFiles;
  };

  const fileUploadAWS = async (files: any) => {
    await dispatch(
      uploadFileAction({
        files: files,
        progress: setProgressCount,
        progressCount: progressCount,
        totalFiles: fileUploads?.length
      })
    );
  };

  const onSubmit = async (params: any) => {
    const exteriorFields = exteriorCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    exteriorFields['urls'] = fileUploadHook(exteriorFields, params.uploads);

    const interiorFields = interiorCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    interiorFields['urls'] = fileUploadHook(interiorFields, params.uploads);

    const engineFields = engineCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    engineFields['urls'] = fileUploadHook(engineFields, params.uploads);

    const underCarriageFields = underCarriageCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    underCarriageFields['urls'] = fileUploadHook(underCarriageFields, params.uploads);

    const safetyFields = safetyCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    safetyFields['urls'] = fileUploadHook(safetyFields, params.uploads);

    const vehicleFields = vehicleCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    vehicleFields['urls'] = fileUploadHook(vehicleFields, params.uploads);

    const complianceFields = complianceCheckboxes.reduce((obj, field) => {
      obj[field.field] = params[field.field];
      return obj;
    }, {} as any);
    complianceFields['urls'] = fileUploadHook(complianceFields, params.uploads);

    const currentDate = new Date();
    const result = {
      inspectorName: params.inspectorName,
      inspectionId: convertToEpoch(currentDate),
      inspectionDate: convertToEpoch(params.dateOfInspection),
      vehicleNumber: params.vin,
      exteriorInspection: exteriorFields,
      interiorInspection: interiorFields,
      engineCompartment: engineFields,
      undercarriageInspection: underCarriageFields,
      safetyEquipment: safetyFields,
      vehicleDocumentation: vehicleFields,
      complianceAndEmissions: complianceFields
    };
    setInspector(params.inspectorName);
    setInspectorId(convertToEpoch(currentDate));
    setFileUploads(params.uploads);
    await dispatch(setInspectionDetails(result));

    setTimeout(() => {
      setInspection(false);
    }, 7000);

    // clearTimeout(id);
  };

  const { append } = useFieldArray({
    control,
    name: 'uploads'
  });

  const { fields } = useFieldArray({
    control,
    name: 'descriptions'
  });

  const handleUpload = async (
    boxindex: number,
    categoryList: any,
    cardtitle: string,
    cardindex: number,
    checkboxField: string
  ) => {
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.multiple = true;
    inputElement.accept = 'image/*';

    inputElement.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      let compressedFile = await imageCompression(file, {
        maxSizeMB: 0.05,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      });

      if (compressedFile.size > 50 * 1024) {
        compressedFile = await imageCompression(file, {
          maxSizeMB: 0.05,
          maxWidthOrHeight: 1024,
          useWebWorker: true
        });
      }

      const fileName = compressedFile.name;

      setCardDetails((prev: any) => {
        let updatedCards = [...prev];
        let newCards = updatedCards
          ?.filter(cards => {
            if (cards.title === cardtitle) return cards;
          })
          .map((file: any) => ({
            ...file,
            categoryList: categoryList?.map((item: any, index: number) => ({
              ...item,
              document: index === boxindex ? fileName : item.document,
              documentFile: index === boxindex ? compressedFile : item.documentFile
            }))
          }));
        updatedCards[cardindex] = newCards[0];
        return updatedCards;
      });

      append({
        document: compressedFile.name,
        documentFile: compressedFile,
        field: checkboxField
      });
      clearErrors();
    };

    inputElement.click();
  };

  useEffect(() => {
    if (data) {
      const filters = Object.entries(data)
        .flat()
        .filter(item => typeof item !== 'string');
      const fileUpload = fileUploads
        ?.map((item: any) => {
          let payloads = filters?.map((value: any) => {
            if (Object.keys(value).includes(item.field)) {
              return {
                file: item?.documentFile,
                url: value[item?.field][item?.document]
              };
            }
          });
          return payloads;
        })
        .flat()
        .filter((file: any) => file !== undefined);
      fileUpload.forEach((item: any) => fileUploadAWS(item));
    }
  }, [data]);

  useEffect(() => {
    dispatch(truckNumbers(createAbort().abortCall.signal));
    return () => {
      createAbort().abortCall.abort();
    };
  }, []);

  useEffect(() => {
    if (setInspectionName && setInspectionId) {
      setInspectionName(inspector);
      setInspectionId(inspectorId);
    }
  }, [inspector, inspectorId]);

  useEffect(() => {
    if (filteredRow) {
      let inspectionKeys = [
        'engineCompartment',
        'exteriorInspection',
        'interiorInspection',
        'safetyEquipment',
        'undercarriageInspection',
        'vehicleDocumentation',
        'complianceAndEmissions'
      ];
      setValue('dateOfInspection', filteredRow?.inspectionReport?.date);
      setValue('vehicleNumber', filteredRow?.inspectionReport?.vehicleNumber);
      setValue('inspectorName', filteredRow?.inspectionReport?.inspectorName);

      setCardDetails((cards: any, cardIndex: number) => {
        const updatedCards = cards?.map((cardvalue: any, index: number) => {
          if (inspectionKeys.includes(cardvalue?.field)) {
            let { notes, urls, ...restValue } =
              filteredRow?.inspectionReport[cardvalue?.field];
            let selectAllValue = [...(new Set(Object.values(restValue)) as any)];

            return {
              ...cardvalue,
              category: cardvalue?.category?.map(
                (allCategory: any, allCategoryIndex: number) => ({
                  ...allCategory,
                  checked:
                    selectAllValue?.length === 1 &&
                    allCategory.value === selectAllValue.join()
                      ? true
                      : false
                })
              ),
              categoryList: cardvalue?.categoryList?.map(
                (cardCategory: any, cardCategoryIndex: number) => {
                  const category = Object.keys(
                    filteredRow?.inspectionReport[cardvalue?.field]
                  );

                  if (category?.includes(cardCategory.field)) {
                    return {
                      ...cardCategory,
                      document:
                        filteredRow?.inspectionReport[cardvalue?.field].urls[
                          `${cardCategory?.name}img`
                        ],
                      categorycheck: cardCategory?.categorycheck?.map(
                        (cardCheck: any, cardCheckIndex: number) => ({
                          ...cardCheck,
                          checked:
                            cardCheck.name ===
                            filteredRow.inspectionReport[cardvalue.field][
                              cardCategory.field
                            ]
                              ? true
                              : false
                        })
                      )
                    };
                  }
                }
              )
            };
          }
        });
        return updatedCards;
      });
    }
  }, [viewInspection, filteredRow]);

  return (
    <Box className='Inspection-View' sx={{ overflowX: 'scroll' }}>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        className='inspection-Form'
        sx={{
          padding: { xs: '20px', sm: '40px' }
        }}
      >
        <Typography variant='h4' className='Inspection-Heading' gutterBottom>
          Vehicle Inspection Form
        </Typography>
        <Box
          className='Vechicle [...newCards];-Form'
          sx={{
            mt: {
              xs: '10px',
              sm: '10px'
            }
          }}
        >
          <Typography
            variant='h6'
            gutterBottom
            sx={{
              color: '#26134b',
              fontWeight: 600,
              textAlign: { xs: 'center', sm: 'left' },
              marginBottom: '40px'
            }}
          >
            Vehicle Information
          </Typography>
          <Grid container spacing={1} className='vehicle-box'>
            {textField.map(({ id, label, placeholder }) => (
              <Grid
                item
                xs={12}
                sm={3}
                lg={4}
                key={id}
                marginTop={
                  !viewInspection && (id === 'dateOfInspection' || id === 'vin')
                    ? '-7px'
                    : '0px'
                }
              >
                {id === 'dateOfInspection' ? (
                  <CustomDateCalendar
                    id={id}
                    name={id}
                    control={control}
                    label={label}
                    isDisabled={viewInspection ? true : false}
                    value={
                      filteredRow
                        ? dayjs.unix(
                            filteredRow?.inspectionReport?.date?.toString().length > 10
                              ? filteredRow?.inspectionReport?.date / 1000000
                              : filteredRow?.inspectionReport?.date
                          )
                        : null
                    }
                    disablePast={true}
                    placeholder={placeholder}
                    onDateChange={(date: Date) => handleFromDate(date)}
                  />
                ) : id === 'vin' ? (
                  <CustomSelect
                    options={vehicleDetails}
                    id={id}
                    name={id}
                    isDisabled={viewInspection ? true : false}
                    className='inspection-select'
                    control={control}
                    label={label}
                    placeholder={placeholder}
                  />
                ) : (
                  <CustomTextField
                    id={id}
                    name={id}
                    disabled={viewInspection ? true : false}
                    control={control}
                    label={label}
                    placeholder={placeholder}
                    onChangeCallback={(e: any) => {}}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{ mt: 5, mb: 5 }}>
          {cardDetails?.map((cards: any, cardindex: number) => (
            <>
              <CustomMultiCard title={cards.title}>
                <Box display={'flex'} justifyContent={'end'}>
                  {cards?.category?.map((category: any, categoryindex: number) => (
                    <CustomCheckBox
                      name={category?.name}
                      label={category?.label}
                      control={control}
                      disabled={viewInspection ? true : false}
                      checked={category?.checked}
                      onChange={(e: any) => {
                        handleSelect(
                          category.value,
                          cards?.categoryList,
                          cardindex,
                          cards?.title
                        );
                      }}
                    />
                  ))}
                </Box>
                {cards?.categoryList?.map((checkbox: any, index: number) => (
                  <>
                    <CustomCardcheckbox
                      field={checkbox.field}
                      control={control}
                      name={checkbox.name}
                      disabled={viewInspection ? true : false}
                      description={checkbox.description}
                      checkboxList={checkbox.categorycheck}
                      onChange={(label: any) => {
                        handleCheckbox(
                          label,
                          cards?.categoryList,
                          cardindex,
                          cards?.title,
                          checkbox.name
                        );
                      }}
                    />
                    <Box display='flex' alignItems='center' justifyContent={'end'} mt={1}>
                      {checkbox.document && (
                        <span style={{ color: '#3239ea' }}>{checkbox.document}</span>
                      )}
                      <Icon
                        icon='material-symbols:upload'
                        width='1.5em'
                        height='1.5em'
                        onClick={() =>
                          handleUpload(
                            index,
                            cards.categoryList,
                            cards.title,
                            cardindex,
                            checkbox.field
                          )
                        }
                      />
                    </Box>
                  </>
                ))}
                {fields.map((field: any, index) => (
                  <CustomTextField
                    id={`descriptions[${cardindex}]`}
                    name={`descriptions[${cardindex}].description`}
                    label={`Description`}
                    control={control}
                    placeholder={'Description'}
                    type={'address'}
                  />
                ))}
              </CustomMultiCard>
            </>
          ))}
        </Box>
        {!viewInspection && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}
          >
            <Stack gap={'15px'} flexDirection={'row'}>
              <CustomButton
                category='Submit'
                className='saveChanges'
                type='submit'
                loading={isLoading}
              />
              <CustomButton
                category='Cancel'
                className='cancel'
                onClick={() => {
                  setChecked(false);
                  setInspection(false);
                }}
              />
            </Stack>
            {fileUploads?.length > 0 && (
              <CustomLinearProgress
                progressCount={progressCount}
                totalFiles={fileUploads.length}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InspectionView;
